/**
 * EmailPreviewModal — aperçu HTML complet d'un email avant envoi.
 *
 * Usage :
 *   const preview = useEmailPreview();
 *   preview.open({
 *       template: "onboarding_invite",
 *       candidateId,
 *       role: "responsable_equipe",   // optionnel
 *       confirmLabel: "Lancer l'onboarding",
 *       onConfirm: async () => await api.post(...)
 *   });
 *
 * Le composant s'occupe de :
 *   - Charger `/api/emails/preview`
 *   - Afficher un iframe sandboxé avec le HTML rendu
 *   - Afficher le destinataire, le sujet et un badge si l'envoi sera bloqué
 *   - Appeler onConfirm() sur clic "Envoyer maintenant"
 */
import * as Dialog from "@radix-ui/react-dialog";
import { AlertTriangle, Loader2, Mail, Send, ShieldOff, X } from "lucide-react";
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import { toast } from "sonner";

import { api } from "./api";

const EmailPreviewContext = createContext(null);


export function EmailPreviewProvider({ children }) {
    const [state, setState] = useState(null); // null | {...open payload}
    const [preview, setPreview] = useState(null); // {subject, html, plain, recipient, will_be_blocked, block_reason}
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);

    const close = useCallback(() => {
        setState(null);
        setPreview(null);
        setError(null);
        setSending(false);
    }, []);

    const open = useCallback((payload) => {
        setState(payload || {});
        setPreview(null);
        setError(null);
        setSending(false);
    }, []);

    // Fetch preview whenever a new payload is set
    useEffect(() => {
        if (!state) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        (async () => {
            try {
                const { data } = await api.post("/emails/preview", {
                    template: state.template,
                    candidate_id: state.candidateId,
                    role: state.role || null,
                    contract_id: state.contractId || null,
                });
                if (!cancelled) setPreview(data);
            } catch (e) {
                if (!cancelled) {
                    setError(e?.response?.data?.detail || "Impossible de générer l'aperçu");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [state]);

    // Inject HTML into the iframe via `srcDoc` — reliable across timing
    // scenarios. We wrap the HTML in a minimal document if it's just a
    // fragment (our email renderers return fragments starting with <h1>).
    const iframeSrcDoc = useMemo(() => {
        if (!preview?.html) return null;
        const trimmed = preview.html.trimStart();
        if (/^<!doctype|<html/i.test(trimmed)) return preview.html;
        return `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:#1B1B1B;background:#f5f5f0;padding:24px;} img{max-width:100%;}</style></head><body>${preview.html}</body></html>`;
    }, [preview]);

    const handleConfirm = useCallback(async () => {
        if (!state?.onConfirm) return close();
        setSending(true);
        try {
            await state.onConfirm();
            close();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "Envoi impossible";
            toast.error(msg);
            setSending(false);
        }
    }, [state, close]);

    const value = useMemo(() => ({ open, close }), [open, close]);

    const confirmLabel = state?.confirmLabel || "Envoyer maintenant";
    const titleOverride = state?.title;
    const description = state?.description;

    return (
        <EmailPreviewContext.Provider value={value}>
            {children}
            <Dialog.Root open={!!state} onOpenChange={(v) => !v && close()}>
                <Dialog.Portal>
                    <Dialog.Overlay
                        data-testid="email-preview-overlay"
                        className="fixed inset-0 z-[80] bg-shariti-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in"
                    />
                    <Dialog.Content
                        data-testid="email-preview-dialog"
                        onEscapeKeyDown={(e) => sending && e.preventDefault()}
                        onPointerDownOutside={(e) => sending && e.preventDefault()}
                        className="fixed left-1/2 top-1/2 z-[81] w-[95vw] max-w-3xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white shadow-2xl focus:outline-none flex flex-col max-h-[90vh]"
                    >
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="h-9 w-9 rounded-full bg-shariti-orange-soft flex items-center justify-center">
                                        <Mail className="h-4 w-4 text-shariti-orange" />
                                    </div>
                                    <Dialog.Title className="text-lg font-extrabold text-shariti-black">
                                        {titleOverride || "Aperçu email avant envoi"}
                                    </Dialog.Title>
                                </div>
                                <Dialog.Description className="mt-1 text-xs text-slate-600 font-semibold">
                                    {description || "Vérifiez le contenu — rien n'est envoyé tant que vous n'avez pas confirmé."}
                                </Dialog.Description>
                            </div>
                            <Dialog.Close asChild>
                                <button
                                    type="button"
                                    aria-label="Fermer"
                                    className="h-8 w-8 shrink-0 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </Dialog.Close>
                        </div>

                        {/* Body */}
                        <div className="p-4 md:p-5 space-y-3 overflow-y-auto flex-1">
                            {loading && (
                                <div className="inline-flex items-center gap-2 text-sm text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin" /> Génération de l&apos;aperçu…
                                </div>
                            )}
                            {error && (
                                <div className="text-sm text-shariti-red p-3 rounded-lg bg-red-50 border border-red-100">
                                    {error}
                                </div>
                            )}
                            {preview && (
                                <>
                                    {/* Meta strip */}
                                    <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 rounded-xl bg-shariti-cream/60 border border-shariti-orange/20 p-3 text-xs">
                                        <div className="font-black uppercase tracking-widest text-shariti-orange">À</div>
                                        <div className="text-shariti-black font-bold" data-testid="email-preview-recipient">{preview.recipient || "—"}</div>
                                        <div className="font-black uppercase tracking-widest text-shariti-orange">Objet</div>
                                        <div className="text-shariti-black font-extrabold" data-testid="email-preview-subject">{preview.subject}</div>
                                    </div>

                                    {/* Block warning */}
                                    {preview.will_be_blocked && (
                                        <div
                                            data-testid="email-preview-blocked-banner"
                                            className="rounded-xl border-2 border-amber-300 bg-amber-50 p-3 flex items-start gap-2"
                                        >
                                            <ShieldOff className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                                            <div className="text-xs font-bold text-amber-900 leading-relaxed">
                                                <div className="font-extrabold uppercase tracking-widest text-[10px] mb-0.5">Envoi bloqué</div>
                                                {preview.block_reason}
                                            </div>
                                        </div>
                                    )}

                                    {/* HTML iframe */}
                                    <iframe
                                        key={preview.subject}
                                        data-testid="email-preview-iframe"
                                        title="Aperçu email"
                                        sandbox=""
                                        srcDoc={iframeSrcDoc || ""}
                                        className="w-full h-[420px] rounded-xl border-2 border-slate-200 bg-white"
                                    />
                                </>
                            )}
                        </div>

                        {/* Footer actions */}
                        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3">
                            <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                                {preview?.will_be_blocked ? "Aperçu uniquement" : "Prêt à envoyer"}
                            </div>
                            <div className="flex items-center gap-2">
                                <Dialog.Close asChild>
                                    <button
                                        type="button"
                                        data-testid="email-preview-cancel"
                                        disabled={sending}
                                        className="h-10 px-4 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm transition disabled:opacity-50"
                                    >
                                        Annuler
                                    </button>
                                </Dialog.Close>
                                <button
                                    type="button"
                                    data-testid="email-preview-confirm"
                                    onClick={handleConfirm}
                                    disabled={loading || sending || !!error}
                                    className={`h-10 px-4 rounded-full font-extrabold text-sm inline-flex items-center gap-1.5 shadow-btn transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                        preview?.will_be_blocked
                                            ? "bg-amber-500 text-white hover:brightness-110"
                                            : "bg-gradient-to-br from-shariti-orange to-[#d66528] text-white hover:brightness-110"
                                    }`}
                                >
                                    {sending
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                        : (preview?.will_be_blocked ? <AlertTriangle className="h-3.5 w-3.5" /> : <Send className="h-3.5 w-3.5" />)}
                                    <span>
                                        {sending ? "Envoi…" : (preview?.will_be_blocked ? "Confirmer quand même" : confirmLabel)}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </Dialog.Content>
                </Dialog.Portal>
            </Dialog.Root>
        </EmailPreviewContext.Provider>
    );
}


export function useEmailPreview() {
    const ctx = useContext(EmailPreviewContext);
    if (!ctx) {
        // Safe no-op so components outside the provider (login page) don't crash.
        return { open: () => {}, close: () => {} };
    }
    return ctx;
}
